import React from "react";
import { Modal, Row, Col } from "react-bootstrap";
import CustomizedDropdown from "components/wrapped-component/customized-dropdown/CustomizedDropdown";
import Button from "components/wrapped-component/hint-controls/Button";
import { httpDieType } from "services/http.die-types";
import { DieType as SystemDieType } from "components/utility-component/wafer-map-widget/wafer-map/web-gl-utils/Types";
import DieTypeMappingDataTable from "./die-type-mapping-data-table";
import "./die-type-mapping-popup.scss";

// Die type name from API (string, not the limited parser DieType)
type DieTypeName = string;

interface DieTypeMappingPopupProps {
  show: boolean;
  uniqueCharacters: string[];
  parsedFileData: any; // WaferMapParserResult
  onClose: () => void;
  history: any;
}

interface CharacterMapping {
  dieType: DieTypeName;
  hardBinNo: string;
  isHardBinNoChecked: boolean;
  hardBinName: string;
  softBinNo: string;
  isSoftBinNoChecked: boolean;
  softBinName: string;
  pfFlag: string;
  isPFFlagChecked: boolean;
}

interface DieTypeMappingPopupState {
  mappings: Record<string, CharacterMapping>;
  binMap: "None" | "User Defined";
  headerCheckboxes: {
    hardBinNo: boolean;
    hardBinName: boolean;
    softBinNo: boolean;
    softBinName: boolean;
    pfFlag: boolean;
  };
  dieTypes: SystemDieType[];
  isLoadingDieTypes: boolean;
  dieTypeOptions: Array<[string, string]>;
}

const BIN_MAP_OPTIONS: Array<["None" | "User Defined", string]> = [
  ["None", "None"],
  ["User Defined", "User Defined"],
];

class DieTypeMappingPopup extends React.Component<
  DieTypeMappingPopupProps,
  DieTypeMappingPopupState
> {
  constructor(props: DieTypeMappingPopupProps) {
    super(props);
    this.state = {
      mappings: {},
      binMap: "None",
      headerCheckboxes: {
        hardBinNo: false,
        hardBinName: false,
        softBinNo: false,
        softBinName: false,
        pfFlag: false,
      },
      dieTypes: [],
      isLoadingDieTypes: false,
      dieTypeOptions: [],
    };
  }

  componentDidMount() {
    this.loadDieTypes();
  }

  componentDidUpdate(
    prevProps: DieTypeMappingPopupProps,
    prevState: DieTypeMappingPopupState
  ) {
    // Initialize mappings when popup opens
    if (this.props.show && !prevProps.show) {
      this.initializeMappings();
    }

    // Re-initialize mappings if die types just finished loading and mappings are empty
    if (
      this.props.show &&
      prevState.isLoadingDieTypes &&
      !this.state.isLoadingDieTypes &&
      this.state.dieTypes.length > 0 &&
      Object.keys(this.state.mappings).length === 0
    ) {
      this.initializeMappings();
    }
  }

  initializeMappings = () => {
    const initialMappings: Record<string, CharacterMapping> = {};
    this.props.uniqueCharacters.forEach((char) => {
      // Set default based on character value - try to match with system die types
      let defaultDieTypeName = "Probed Die"; // Default to 'Probed Die' if available
      if (char === "." || char === " ") {
        defaultDieTypeName = "Cropped";
      } else if (char === "p" || char === "o") {
        defaultDieTypeName = "Partial";
      }

      // Find preferred default die types in order of priority
      // Priority: Unassigned > Probed Die > any non-"Unidentified" die type > first available
      let defaultDieType: string | undefined;

      // First, try to find "Unassigned"
      defaultDieType = this.state.dieTypes.find(
        (dt) => dt.name.toLowerCase() === "unassigned"
      )?.name;

      // If not found, try "Probed Die"
      if (!defaultDieType) {
        defaultDieType = this.state.dieTypes.find(
          (dt) =>
            dt.name.toLowerCase() === "probed die" ||
            dt.name.toLowerCase() === "probe die"
        )?.name;
      }

      // If still not found, try any die type that doesn't contain "Unidentified"
      if (!defaultDieType) {
        defaultDieType = this.state.dieTypes.find(
          (dt) => !dt.name.toLowerCase().includes("unidentified")
        )?.name;
      }

      // Final fallback: use first available die type
      if (!defaultDieType && this.state.dieTypes.length > 0) {
        defaultDieType = this.state.dieTypes[0].name;
      }

      // If still no default, use the character-based default
      if (!defaultDieType) {
        defaultDieType = defaultDieTypeName;
      }

      initialMappings[char] = {
        dieType: defaultDieType || "",
        hardBinNo: "",
        isHardBinNoChecked: false,
        hardBinName: "",
        softBinNo: "",
        isSoftBinNoChecked: false,
        softBinName: "",
        pfFlag: "",
        isPFFlagChecked: false,
      };
    });
    this.setState({ mappings: initialMappings });
  };

  loadDieTypes = async () => {
    this.setState({ isLoadingDieTypes: true });
    try {
      const dieTypesResponse = await httpDieType.getDieTypes();
      // Handle different response formats - could be array directly or wrapped in data property
      let dieTypes: SystemDieType[] = [];
      if (Array.isArray(dieTypesResponse)) {
        dieTypes = dieTypesResponse;
      } else if (dieTypesResponse && Array.isArray(dieTypesResponse.data)) {
        dieTypes = dieTypesResponse.data;
      } else if (dieTypesResponse && dieTypesResponse.data) {
        dieTypes = [dieTypesResponse.data];
      }

      // Convert die types to dropdown options format: [value, label]
      const dieTypeOptions: Array<[string, string]> = dieTypes.map((dt) => [
        dt.name,
        dt.name,
      ]);

      this.setState({
        dieTypes,
        dieTypeOptions,
        isLoadingDieTypes: false,
      });
    } catch (error) {
      console.error("Error loading die types:", error);
      this.setState({ isLoadingDieTypes: false });
      // Fallback to empty options on error
    }
  };

  handleDieTypeChange = (character: string, dieType: string) => {
    // Ensure dieType is a string and trim it
    const dieTypeName = String(dieType).trim();

    this.setState((prevState) => ({
      mappings: {
        ...prevState.mappings,
        [character]: {
          ...prevState.mappings[character],
          dieType: dieTypeName,
        },
      },
    }));
  };

  handleCheckboxChange = (
    character: string,
    field: keyof CharacterMapping,
    checked: boolean
  ) => {
    if (this.state.binMap === "None") {
      return; // Don't allow changes when Bin Map is None
    }

    this.setState((prevState) => ({
      mappings: {
        ...prevState.mappings,
        [character]: {
          ...prevState.mappings[character],
          [field]: checked,
        },
      },
    }));
  };

  handleDropdownChange = (
    character: string,
    field: keyof CharacterMapping,
    value: string
  ) => {
    this.setState((prevState) => ({
      mappings: {
        ...prevState.mappings,
        [character]: {
          ...prevState.mappings[character],
          [field]: value,
        },
      },
    }));
  };

  handleInputChange = (
    character: string,
    field: keyof CharacterMapping,
    value: string
  ) => {
    this.setState((prevState) => ({
      mappings: {
        ...prevState.mappings,
        [character]: {
          ...prevState.mappings[character],
          [field]: value,
        },
      },
    }));
  };

  handleBinMapChange = (value: string) => {
    const binMap = value as "None" | "User Defined";
    this.setState({ binMap });

    // If set to None, uncheck all header checkboxes and disable all bin fields
    if (binMap === "None") {
      this.setState({
        headerCheckboxes: {
          hardBinNo: false,
          hardBinName: false,
          softBinNo: false,
          softBinName: false,
          pfFlag: false,
        },
      });

      // Uncheck all row checkboxes for bin fields
      const updatedMappings: Record<string, CharacterMapping> = {};
      Object.entries(this.state.mappings).forEach(([char, mapping]) => {
        updatedMappings[char] = {
          ...mapping,
          isHardBinNoChecked: false,
          isSoftBinNoChecked: false,
          isPFFlagChecked: false,
        };
      });
      this.setState({ mappings: updatedMappings });
    }
  };

  handleHeaderCheckboxChange = (
    field: keyof DieTypeMappingPopupState["headerCheckboxes"],
    checked: boolean
  ) => {
    if (this.state.binMap === "None") {
      return; // Don't allow changes when Bin Map is None
    }

    this.setState((prevState) => ({
      headerCheckboxes: {
        ...prevState.headerCheckboxes,
        [field]: checked,
      },
    }));

    // Update all rows based on header checkbox
    const updatedMappings: Record<string, CharacterMapping> = {};
    Object.entries(this.state.mappings).forEach(([char, mapping]) => {
      let updatedMapping = { ...mapping };

      if (field === "hardBinNo") {
        updatedMapping.isHardBinNoChecked = checked;
      } else if (field === "softBinNo") {
        updatedMapping.isSoftBinNoChecked = checked;
      } else if (field === "pfFlag") {
        updatedMapping.isPFFlagChecked = checked;
      }

      updatedMappings[char] = updatedMapping;
    });
    this.setState({ mappings: updatedMappings });
  };

  handleApply = () => {
    // Extract die type mappings
    const dieTypeMappings: Record<string, DieTypeName> = {};
    const dieTypeMappingArray: Array<{
      character: string;
      dieType: DieTypeName;
      hardBinNo: string | null;
      hardBinName: string | null;
      softBinNo: string | null;
      softBinName: string | null;
      pfFlag: string | null;
    }> = [];

    Object.entries(this.state.mappings).forEach(([char, mapping]) => {
      // Ensure dieType is trimmed and valid
      const dieTypeName = String(mapping.dieType || "").trim();
      dieTypeMappings[char] = dieTypeName;

      // Create array entry with all character properties including bin fields and P/F flag
      // Always include all fields, set to null if not entered or checkbox not checked
      const characterMapping: {
        character: string;
        dieType: DieTypeName;
        hardBinNo: string | null;
        hardBinName: string | null;
        softBinNo: string | null;
        softBinName: string | null;
        pfFlag: string | null;
      } = {
        character: char,
        dieType: dieTypeName,
        // Always include all fields - set to null if not entered or checkbox not checked
        hardBinNo:
          this.state.headerCheckboxes.hardBinNo &&
          mapping.hardBinNo &&
          mapping.hardBinNo.trim() !== ""
            ? mapping.hardBinNo.trim()
            : null,
        hardBinName:
          this.state.headerCheckboxes.hardBinName &&
          mapping.hardBinName &&
          mapping.hardBinName.trim() !== ""
            ? mapping.hardBinName.trim()
            : null,
        softBinNo:
          this.state.headerCheckboxes.softBinNo &&
          mapping.softBinNo &&
          mapping.softBinNo.trim() !== ""
            ? mapping.softBinNo.trim()
            : null,
        softBinName:
          this.state.headerCheckboxes.softBinName &&
          mapping.softBinName &&
          mapping.softBinName.trim() !== ""
            ? mapping.softBinName.trim()
            : null,
        pfFlag:
          this.state.headerCheckboxes.pfFlag &&
          mapping.pfFlag &&
          mapping.pfFlag.trim() !== ""
            ? mapping.pfFlag.trim()
            : null,
      };

      dieTypeMappingArray.push(characterMapping);
    });

    // Update allCharacters with die type mappings
    const updatedAllCharacters = this.props.parsedFileData.allCharacters.map(
      (charData: any) => ({
        ...charData,
        dieType: dieTypeMappings[charData.char] || charData.dieType,
      })
    );

    // Prepare data to pass to wafer-control-map screen
    // Only include what's actually needed: metadata, allCharacters, and die type mappings
    const waferMapData = {
      metadata: this.props.parsedFileData.metadata,
      allCharacters: updatedAllCharacters,
      dieTypeMappings,
      dieTypeMappingArray,
    };

    // Navigate to wafer-control-map with data
    this.props.history.push({
      pathname: "/wafer-control-map",
      state: { parsedFileData: waferMapData },
    });

    this.props.onClose();
  };

  handleClose = () => {
    this.props.onClose();
  };

  render() {
    const { show, uniqueCharacters } = this.props;
    const {
      mappings,
      isLoadingDieTypes,
      dieTypeOptions,
      binMap,
      headerCheckboxes,
    } = this.state;

    return (
      <Modal
        show={show}
        onHide={this.handleClose}
        dialogClassName="die-type-mapping-modal"
        aria-labelledby="die-type-mapping-modal-title"
        backdrop="static"
        centered
      >
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title id="die-type-mapping-modal-title">
            Die Type Mapping
          </Modal.Title>
          <div className="bin-map-container">
            <label className="bin-map-label">Bin Map:</label>
            <CustomizedDropdown
              variant="clear"
              list={BIN_MAP_OPTIONS}
              selectedValue={binMap}
              onChange={this.handleBinMapChange}
            />
          </div>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col>
              <DieTypeMappingDataTable
                uniqueCharacters={uniqueCharacters}
                mappings={mappings}
                dieTypeOptions={dieTypeOptions}
                isLoadingDieTypes={isLoadingDieTypes}
                binMap={binMap}
                headerCheckboxes={headerCheckboxes}
                onDieTypeChange={this.handleDieTypeChange}
                onInputChange={this.handleInputChange}
                onDropdownChange={this.handleDropdownChange}
                onHeaderCheckboxChange={this.handleHeaderCheckboxChange}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-top">
          <Button variant="primary" onClick={this.handleApply}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default DieTypeMappingPopup;

